import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  IconButton,
  Alert,
  TextField,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Grid,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DescriptionIcon from '@mui/icons-material/Description';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { checkBookService, CheckBook, CheckBookSummary } from '../../../services/checkBookService';
import AddCheckBookModal from './AddCheckBookModal';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import { useTranslation } from 'react-i18next';

const CheckBooks: React.FC = () => {
  const { i18n } = useTranslation('accounting');
  const isTr = i18n.language?.toLowerCase().startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userDetails } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // URL'den gelen filtre parametreleri
  const filterBankAccountId = searchParams.get('bankAccountId');
  const filterSubAccountId = searchParams.get('subAccountId');
  const isFiltered = !!(filterBankAccountId || filterSubAccountId);

  // State'ler
  const [checkBooks, setCheckBooks] = useState<CheckBook[]>([]);
  const [filteredCheckBooks, setFilteredCheckBooks] = useState<CheckBook[]>([]);
  const [summary, setSummary] = useState<CheckBookSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal state'leri
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [checkBookToEdit, setCheckBookToEdit] = useState<CheckBook | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCheckBook, setSelectedCheckBook] = useState<CheckBook | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Satır bazlı 3 nokta menüsü
  const [anchorElRow, setAnchorElRow] = useState<{ [key: number]: HTMLElement | null }>({});

  const handleOpenRowMenu = (checkBook: CheckBook, event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorElRow(prev => ({ ...prev, [checkBook.id]: event.currentTarget }));
  };

  const handleCloseRowMenu = (checkBookId: number) => {
    setAnchorElRow(prev => ({ ...prev, [checkBookId]: null }));
  };

  // Veri yükle
  const loadData = async () => {
    if (!userDetails?.companyId) return;

    setLoading(true);
    setError(null);

    try {
      const [checkBooksData, summaryData] = await Promise.all([
        checkBookService.getByCompany(userDetails.companyId),
        checkBookService.getSummary(userDetails.companyId)
      ]);

      setCheckBooks(checkBooksData);
      setFilteredCheckBooks(checkBooksData);
      setSummary(summaryData);
    } catch (err) {
      console.error('Çek defterleri yüklenirken hata:', err);
      setError(tt('Çek defterleri yüklenirken bir hata oluştu', 'An error occurred while loading checkbooks'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [userDetails?.companyId]);

  // URL parametresine göre ve arama filtresine göre filtrele
  useEffect(() => {
    let filtered = checkBooks;

    // URL parametresine göre filtrele (hesaba özel görünüm)
    if (filterSubAccountId) {
      // SubAccount ID'ye göre filtrele
      filtered = filtered.filter(cb => cb.subAccountId === parseInt(filterSubAccountId));
    } else if (filterBankAccountId) {
      // BankAccount ID'ye göre filtrele (subAccount'u olmayanlar)
      filtered = filtered.filter(cb => 
        cb.bankAccountId === parseInt(filterBankAccountId) && !cb.subAccountId
      );
    }

    // Arama terimine göre filtrele
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(cb => 
        cb.serialNumber.toLowerCase().includes(term) ||
        cb.bankAccount?.bankName?.toLowerCase().includes(term) ||
        cb.subAccount?.accountName?.toLowerCase().includes(term) ||
        cb.description?.toLowerCase().includes(term)
      );
    }

    setFilteredCheckBooks(filtered);
  }, [searchTerm, checkBooks, filterBankAccountId, filterSubAccountId]);

  // Silme işlemi
  const handleDelete = async () => {
    if (!selectedCheckBook || !userDetails?.companyId) return;

    setIsDeleting(true);
    try {
      await checkBookService.delete(selectedCheckBook.id, userDetails.companyId);
      setDeleteDialogOpen(false);
      setSelectedCheckBook(null);
      loadData();
    } catch (err: any) {
      setError(err.message || tt('Çek defteri silinemedi', 'Checkbook could not be deleted'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Düzenleme
  const handleEdit = (checkBook: CheckBook) => {
    setCheckBookToEdit(checkBook);
    setIsAddModalOpen(true);
  };

  // Modal kapatma
  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setCheckBookToEdit(null);
    loadData();
  };

  // Para formatı
  const formatCurrency = (amount: number, currency: string = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Yüzde hesapla
  const getUsagePercentage = (checkBook: CheckBook) => {
    if (checkBook.totalLeaves === 0) return 0;
    return Math.round((checkBook.usedLeaves / checkBook.totalLeaves) * 100);
  };

  // Durum rengi
  const getStatusColor = (checkBook: CheckBook): 'error' | 'warning' | 'success' => {
    const percentage = getUsagePercentage(checkBook);
    if (percentage >= 100) return 'error';
    if (percentage >= 75) return 'warning';
    return 'success';
  };

  // AccountingDataTable kolon tanımları
  const checkBookColumns = useMemo<DataTableColumn<CheckBook>[]>(() => [
    {
      id: 'serialNumber',
      label: tt('Seri No', 'Serial No'),
      field: 'serialNumber',
      sortable: true,
      render: (row) => (
        <Box>
          <Typography fontWeight="bold">{row.serialNumber}</Typography>
          {row.description && <Typography variant="caption" color="textSecondary">{row.description}</Typography>}
        </Box>
      ),
    },
    {
      id: 'bankName',
      label: tt('Hesap', 'Account'),
      sortable: true,
      render: (row) => (
        <Box display="flex" alignItems="center" gap={1}>
          <AccountBalanceIcon fontSize="small" color="primary" />
          <Box>
            <Typography variant="body2">
              {row.subAccountId && row.subAccount
                ? `${row.bankAccount?.bankName || ''} - ${row.subAccount.accountName}`
                : row.bankAccount?.bankName || '-'}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {row.subAccountId && row.subAccount
                ? row.subAccount.accountNumber || '-'
                : row.bankAccount?.accountNumber || '-'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'startNumber',
      label: tt('Numara Aralığı', 'Number Range'),
      align: 'center',
      sortable: true,
      render: (row) => (
        <Box>
          <Typography variant="body2">{row.startNumber} - {row.endNumber}</Typography>
          <Typography variant="caption" color="textSecondary">({row.totalLeaves} {tt('yaprak', 'leaves')})</Typography>
        </Box>
      ),
    },
    {
      id: 'usedLeaves',
      label: tt('Yaprak Durumu', 'Leaf Status'),
      align: 'center',
      sortable: true,
      render: (row) => {
        const usagePercentage = getUsagePercentage(row);
        const statusColor = getStatusColor(row);
        const remainingLeaves = row.totalLeaves - row.usedLeaves;
        return (
          <Box sx={{ width: '100%', maxWidth: 150, mx: 'auto' }}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="caption">{row.usedLeaves} / {row.totalLeaves}</Typography>
              <Typography variant="caption" fontWeight="bold">%{usagePercentage}</Typography>
            </Box>
            <LinearProgress variant="determinate" value={usagePercentage} color={statusColor} sx={{ height: 8, borderRadius: 4 }} />
            <Typography variant="caption" color="textSecondary">{tt('Kalan', 'Remaining')}: {remainingLeaves}</Typography>
          </Box>
        );
      },
    },
    {
      id: 'nextAvailableNumber',
      label: tt('Sıradaki No', 'Next No'),
      align: 'center',
      sortable: true,
      render: (row) => getUsagePercentage(row) >= 100
        ? <Chip label={tt('Tükendi', 'Exhausted')} size="small" color="error" />
        : <Typography variant="h6" fontWeight="bold" color="primary.main">{row.nextAvailableNumber}</Typography>,
    },
    {
      id: 'isActive',
      label: tt('Durum', 'Status'),
      align: 'center',
      sortable: true,
      render: (row) => {
        if (!row.isActive) return <Chip label={tt('Pasif', 'Passive')} size="small" color="default" />;
        return getUsagePercentage(row) >= 100
          ? <Chip label={tt('Tamamlandı', 'Completed')} size="small" color="default" />
          : <Chip label={tt('Aktif', 'Active')} size="small" color="success" />;
      },
    },
    {
      id: 'actions',
      label: tt('İşlemler', 'Actions'),
      align: 'right',
      render: (row) => (
        <Box>
          <IconButton size="small" onClick={(e) => handleOpenRowMenu(row, e)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorElRow[row.id]}
            open={Boolean(anchorElRow[row.id])}
            onClose={() => handleCloseRowMenu(row.id)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => { handleCloseRowMenu(row.id); navigate(`/checkbooks/${row.id}/leaves`); }}>
              <ListItemIcon><DescriptionIcon fontSize="small" color="info" /></ListItemIcon>
              <ListItemText primary={tt('Yapraklar', 'Leaves')} />
            </MenuItem>
            <MenuItem onClick={() => { handleCloseRowMenu(row.id); handleEdit(row); }}>
              <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText primary={tt('Düzenle', 'Edit')} />
            </MenuItem>
            <MenuItem disabled={row.usedLeaves > 0} onClick={() => { handleCloseRowMenu(row.id); setSelectedCheckBook(row); setDeleteDialogOpen(true); }}>
              <ListItemIcon><DeleteIcon fontSize="small" color={row.usedLeaves > 0 ? 'disabled' : 'error'} /></ListItemIcon>
              <ListItemText primary={tt('Sil', 'Delete')} secondary={row.usedLeaves > 0 ? tt('Kullanılmış defter silinemez', 'Used checkbook cannot be deleted') : undefined} />
            </MenuItem>
          </Menu>
        </Box>
      ),
    },
  ], [anchorElRow, filteredCheckBooks, i18n.language]);

  // Koşullu satır stili (pasif defterler için)
  const getCheckBookRowSx = (row: CheckBook) => ({
    opacity: row.isActive ? 1 : 0.6,
  });

  // Filtrelenmiş verilerden özet hesapla
  const filteredSummary = useMemo(() => {
    // Filtre yoksa orijinal summary'yi kullan
    if (!isFiltered && summary) {
      return summary;
    }

    // Filtrelenmiş çek defterlerinden özet hesapla
    const totalCheckBooks = filteredCheckBooks.length;
    const activeCheckBooks = filteredCheckBooks.filter(cb => cb.isActive).length;
    const totalLeaves = filteredCheckBooks.reduce((sum, cb) => sum + cb.totalLeaves, 0);
    const usedLeaves = filteredCheckBooks.reduce((sum, cb) => sum + cb.usedLeaves, 0);
    const remainingLeaves = totalLeaves - usedLeaves;

    return {
      totalCheckBooks,
      activeCheckBooks,
      totalLeaves,
      usedLeaves,
      remainingLeaves
    };
  }, [filteredCheckBooks, isFiltered, summary]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Başlık */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <MenuBookIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {tt('Çek Defterleri', 'Check Books')}
            </Typography>
            {isFiltered && (
              <Typography variant="caption" color="text.secondary">
                {tt('Filtrelenmiş görünüm', 'Filtered view')} - {filteredCheckBooks.length} {tt('kayıt', 'records')}
              </Typography>
            )}
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          {isFiltered && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate('/checkbooks')}
            >
              {tt('Tümünü Göster', 'Show All')}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
          >
            {tt('Yenile', 'Refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsAddModalOpen(true)}
          >
            {tt('Yeni Çek Defteri', 'New Check Book')}
          </Button>
        </Box>
      </Box>

      {/* Hata mesajı */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Özet Kartları */}
      {filteredSummary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {tt('Toplam Defter', 'Total Checkbooks')}
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {filteredSummary.totalCheckBooks}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {tt('Aktif Defter', 'Active Checkbooks')}
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {filteredSummary.activeCheckBooks}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {tt('Kalan Yaprak', 'Remaining Leaves')}
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="primary.main">
                  {filteredSummary.remainingLeaves}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {tt('Kullanılan Yaprak', 'Used Leaves')}
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  {filteredSummary.usedLeaves}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Arama */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder={tt('Seri numarası, banka adı veya açıklama ile ara...', 'Search by serial number, bank name, or description...')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Paper>

      {/* Tablo */}
      <AccountingDataTable<CheckBook>
        columns={checkBookColumns}
        rows={filteredCheckBooks}
        getRowId={(row) => String(row.id)}
        loading={false}
        pagination={false}
        defaultSortField="serialNumber"
        defaultSortDirection="asc"
        getRowSx={getCheckBookRowSx}
        emptyMessage={searchTerm ? tt('Arama kriterlerine uygun çek defteri bulunamadı', 'No checkbook found matching search criteria') : tt('Henüz çek defteri eklenmemiş', 'No checkbook has been added yet')}
        emptyIcon={<MenuBookIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
        ariaLabel={tt('çek defterleri tablosu', 'check books table')}
        columnResizeStorageKey="check-books"
      />

      {/* Çek Defteri Ekleme/Düzenleme Modal */}
      <AddCheckBookModal
        open={isAddModalOpen}
        onClose={handleCloseModal}
        checkBookToEdit={checkBookToEdit}
      />

      {/* Silme Onay Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{tt('Çek Defteri Sil', 'Delete Check Book')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{selectedCheckBook?.serialNumber}</strong> {tt('seri numaralı çek defterini silmek istediğinizden emin misiniz?', 'Are you sure you want to delete the checkbook with this serial number?')}
            <br /><br />
            {tt('Bu işlem geri alınamaz.', 'This action cannot be undone.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            {tt('İptal', 'Cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? <CircularProgress size={20} /> : tt('Sil', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CheckBooks;
